import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Link2, 
  Paperclip,
  MessageSquare,
  ChevronRight 
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface MobileRecordCardProps {
  record: any;
  fields: any[];
  primaryField?: any;
  onEdit: () => void;
  onDelete: () => void;
  attachmentCount?: number;
  linkCount?: number;
  commentCount?: number;
}

export const MobileRecordCard = ({
  record,
  fields,
  primaryField,
  onEdit,
  onDelete,
  attachmentCount = 0,
  linkCount = 0,
  commentCount = 0,
}: MobileRecordCardProps) => {
  const getPrimaryValue = () => {
    if (primaryField && record.data) {
      return record.data[primaryField.name] || `Record ${record.id.slice(0, 8)}`;
    }
    // Fallback to first text field
    const textField = fields.find(f => f.type === "text" || f.type === "long_text");
    if (textField && record.data) {
      return record.data[textField.name] || `Record ${record.id.slice(0, 8)}`;
    }
    return `Record ${record.id.slice(0, 8)}`;
  };

  const renderFieldValue = (field: any) => {
    const value = record.data?.[field.name];
    if (value === null || value === undefined || value === "") return null;

    switch (field.type) {
      case "checkbox":
        return value ? (
          <Badge variant="secondary" className="bg-primary/10 text-primary text-[10px]">
            ✓ {field.name}
          </Badge>
        ) : null;
      
      case "single_select":
        return (
          <Badge variant="secondary" className="text-[10px]">
            {value}
          </Badge>
        );
      
      case "date":
        return (
          <span className="text-[10px] text-muted-foreground">
            {format(new Date(value), "MMM d")}
          </span>
        );
      
      case "currency":
        return (
          <span className="text-xs font-medium">${Number(value).toFixed(2)}</span>
        );
      
      case "number":
      case "percent":
        return (
          <span className="text-xs">
            {value}{field.type === "percent" ? "%" : ""}
          </span>
        );
      
      default:
        return null;
    }
  };

  // Get secondary fields to display (max 3)
  const secondaryFields = fields
    .filter(f => f.id !== primaryField?.id && f.type !== "long_text" && f.type !== "attachment")
    .slice(0, 3);

  return (
    <Card className="touch-manipulation active:scale-[0.98] transition-transform">
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0" onClick={onEdit}>
            {/* Primary value */}
            <h4 className="font-medium text-sm truncate mb-1">
              {getPrimaryValue()}
            </h4>
            
            {/* Secondary fields */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {secondaryFields.map((field) => {
                const rendered = renderFieldValue(field);
                return rendered ? <div key={field.id}>{rendered}</div> : null;
              })}
            </div>

            {/* Metadata row */}
            <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
              {linkCount > 0 && (
                <span className="flex items-center gap-0.5">
                  <Link2 className="h-3 w-3" />
                  {linkCount}
                </span>
              )}
              {attachmentCount > 0 && (
                <span className="flex items-center gap-0.5">
                  <Paperclip className="h-3 w-3" />
                  {attachmentCount}
                </span>
              )}
              {commentCount > 0 && (
                <span className="flex items-center gap-0.5">
                  <MessageSquare className="h-3 w-3" />
                  {commentCount}
                </span>
              )}
              <span className="ml-auto">
                {format(new Date(record.created_at), "MMM d")}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={onEdit}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem 
                  className="text-destructive focus:text-destructive"
                  onClick={onDelete}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
